<script>
import LandLeaseAttachmentService from "@/services/LandLeaseAttachmentService";
import { EventBus } from "@/libraries/EventBus";

export default {
    props: {
        landLease: { type: Object, required: true },
        initAddForm: { type: Boolean, default: false }
    },
    data() {
        return {
            showAddForm: this.initAddForm,
            saving: false,
            reloading: false,
            newAttachment: {
                land_lease_id: this.landLease.id,
                attachment_name: null,
                attachment_url: null
            }
        };
    },
    methods: {
        async saveAttachment() {
            this.saving = true;
            try {
                await LandLeaseAttachmentService.POST(this.newAttachment);
                this.$notify({
                    message: this.$t("land_leases.notify_attachment_created"),
                    timeout: 5000,
                    icon: "far fa-check",
                    type: "success"
                });
                this.showAddForm = false;
                this.newAttachment = {
                    land_lease_id: this.landLease.id,
                    attachment_name: null,
                    attachment_url: null
                };
                this.reloading = true;
                EventBus.$emit("land-lease-attachments-changed");
            } catch (e) {
                console.log("saveAttachment e", e);
            }
            this.saving = false;
        },
        deleteAttachment(attachment) {
            this.$swal({
                icon: "warning",
                text: this.$t("land_leases.confirm_delete_attachment"),
                showConfirmButton: true,
                showCancelButton: true
            }).then(async confirmed => {
                if (confirmed.isConfirmed) {
                    try {
                        await LandLeaseAttachmentService.DELETE(attachment.id);
                        this.$notify({
                            message: this.$t(
                                "land_leases.notify_attachment_deleted"
                            ),
                            timeout: 5000,
                            icon: "far fa-check",
                            type: "success"
                        });
                        this.reloading = true;
                        EventBus.$emit("land-lease-attachments-changed");
                    } catch (e) {
                        console.log("deleteAttachment e", e);
                    }
                }
            });
        }
    },
    watch: {
        "landLease.land_lease_attachments"() {
            this.reloading = false;
        }
    }
};
</script>

<template>
    <modal size="lg" :show="true" @close="$emit('close')" class="budget-modal">
        <template slot="header">
            {{ $t("land_leases.contract_and_attachments") }}
        </template>

        <template slot="default">
            <!-- Contract -->
            <div class="mb-4">
                <h4 class="mb-2">{{ $t("land_leases.contract") }}</h4>
                <a
                    v-if="landLease.contract_url"
                    :href="landLease.contract_url"
                    target="_blank"
                >
                    <i class="far fa-file-contract fa-fw"></i>
                    {{ $t("land_leases.contract") }}
                </a>
                <span v-else class="text-muted">
                    <i class="far fa-file-contract fa-fw"></i>
                    {{ $t("land_leases.no_contract_specified") }}
                </span>
            </div>

            <!-- Attachments header -->
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h4 class="mb-0">{{ $t("land_leases.attachments") }}</h4>
                <base-button
                    size="sm"
                    type="primary"
                    outline
                    @click="showAddForm = !showAddForm"
                >
                    <i class="far fa-plus"></i>
                    {{ $t("land_leases.add_attachment") }}
                </base-button>
            </div>

            <!-- Add form -->
            <div v-if="showAddForm" class="border rounded p-3 mb-3 bg-lighter">
                <FormulateForm @submit="saveAttachment" v-model="newAttachment">
                    <div class="row">
                        <div class="col-12 col-md-6">
                            <FormulateInput
                                name="attachment_name"
                                type="text"
                                :label="$t('land_leases.attachment_name')"
                                :placeholder="
                                    $t(
                                        'land_leases.placeholder_attachment_name'
                                    )
                                "
                                validation="required"
                                :validation-name="
                                    $t('land_leases.attachment_name')
                                "
                            />
                        </div>
                        <div class="col-12 col-md-6">
                            <FormulateInput
                                name="attachment_url"
                                type="text"
                                :label="$t('land_leases.attachment_url')"
                                :placeholder="
                                    $t('land_leases.placeholder_attachment_url')
                                "
                                validation="required"
                                :validation-name="
                                    $t('land_leases.attachment_url')
                                "
                            />
                        </div>
                    </div>
                    <div class="text-right">
                        <base-button
                            size="sm"
                            type="secondary"
                            outline
                            @click="showAddForm = false"
                        >
                            {{ $t("cancel") }}
                        </base-button>
                        <base-button
                            size="sm"
                            type="primary"
                            :loading="saving"
                            native-type="submit"
                        >
                            {{ $t("save") }}
                        </base-button>
                    </div>
                </FormulateForm>
            </div>

            <!-- Attachments list -->
            <v-loader :loading="reloading" v-if="reloading" />
            <template v-else>
                <div
                    v-if="
                        landLease.land_lease_attachments &&
                            landLease.land_lease_attachments.length
                    "
                >
                    <div
                        v-for="att in landLease.land_lease_attachments"
                        :key="att.id"
                        class="d-flex justify-content-between align-items-center py-2 border-bottom"
                    >
                        <a :href="att.attachment_url" target="_blank">
                            <i class="far fa-paperclip fa-fw"></i>
                            {{ att.attachment_name }}
                        </a>
                        <base-button
                            size="sm"
                            type="danger"
                            outline
                            @click="deleteAttachment(att)"
                        >
                            <i class="far fa-trash"></i>
                        </base-button>
                    </div>
                </div>
                <div v-else class="text-muted">
                    {{ $t("land_leases.no_attachments") }}
                </div>
            </template>
        </template>
    </modal>
</template>
